import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import ApiService from '../../services/ApiService';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const AuthPageContainer = styled.div`
  min-height: 100vh;
  background: black;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  position: relative;
  overflow: hidden;
  overflow-x: hidden;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 1rem;
  }

  @media (max-width: 480px) {
    padding: 0.75rem;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: radial-gradient(circle at 20% 80%, rgba(0, 215, 255, 0.1) 0%, transparent 50%),
                      radial-gradient(circle at 80% 20%, rgba(0, 140, 255, 0.1) 0%, transparent 50%),
                      radial-gradient(circle at 40% 40%, rgba(138, 43, 226, 0.1) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const AuthCard = styled.div`
  background: rgba(30, 30, 30, 0.1);
  border: 2px solid transparent;
  border-image: linear-gradient(135deg, #FFD700 0%, #FF8C00 100%) 1;
  border-radius: 20px;
  padding: 40px;
  max-width: 450px;
  width: 100%;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3), 
              0 0 0 1px rgba(0, 215, 255, 0.1),
              inset 0 1px 0 rgba(255, 255, 255, 0.1);
  
  animation: ${fadeIn} 0.6s ease-out;
  position: relative;
  z-index: 1;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 2rem;
    max-width: 100%;
  }

  @media (max-width: 480px) {
    padding: 1.5rem;
    border-radius: 16px;
  }
`;

const LogoSection = styled.div`
  text-align: center;
  margin-bottom: 30px;
`;

const BrandTitle = styled.h1`
  font-size: clamp(24px, 5vw, 32px);
  font-weight: 900;
  background: linear-gradient(135deg, #FFD700 0%, #FF8C00 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0 0 10px 0;
  letter-spacing: 3px;
`;

const BrandSubtitle = styled.p`
  color: rgba(255, 255, 255, 0.6);
  font-size: clamp(10px, 2vw, 12px);
  margin: 0;
  letter-spacing: 2px;
`;

const Title = styled.h2`
  text-align: center;
  color: #fff;
  margin-bottom: 10px;
  font-size: clamp(18px, 4vw, 24px);
`;

const Description = styled.p`
  text-align: center;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 30px;
  font-size: 14px;
  line-height: 1.6;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InputGroup = styled.div`
  position: relative;
`;

const Input = styled.input`
  width: 100%;
  padding: 16px 20px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  color: #fff;
  font-size: clamp(14px, 2.5vw, 16px);
  transition: all 0.3s ease;
  box-sizing: border-box;
  min-height: 44px;

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }

  &:focus {
    outline: none;
    border-color: #667eea;
    background: rgba(255, 255, 255, 0.08);
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.2);
  }

  &:hover {
    border-color: rgba(102, 126, 234, 0.3);
  }

  @media (max-width: 480px) {
    padding: 12px 16px;
  }
`;

const PasswordRequirements = styled.ul`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  margin: -10px 0 0 0;
  padding-left: 20px;
  list-style-type: none;

  li {
    margin: 4px 0;
    
    &::before {
      content: '•';
      color: #FFD700;
      margin-right: 8px;
    }
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: clamp(14px, 2.5vw, 16px);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 1px;
  min-height: 44px;
  box-sizing: border-box;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 480px) {
    padding: 12px;
  }
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: #FFD700;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.3s ease;
  text-decoration: none;
  padding: 8px 0;
  
  &:hover {
    color: #FF8C00;
    text-decoration: underline;
  }
`;

const Error = styled.div`
  color: #ff6b6b;
  font-size: 14px;
  padding: 12px 16px;
  background: rgba(255, 107, 107, 0.1);
  border: 1px solid rgba(255, 107, 107, 0.2);
  border-radius: 8px;
  margin-top: 10px;
`;

const Success = styled.div`
  color: #51cf66;
  font-size: 14px;
  padding: 12px 16px;
  background: rgba(81, 207, 102, 0.1);
  border: 1px solid rgba(81, 207, 102, 0.2);
  border-radius: 8px;
  margin-top: 10px;
`;

const ResetPassword = ({ onBackToLogin, resetCode }) => {
  const [formData, setFormData] = useState({
    password: '',
    passwordConfirmation: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState(resetCode || '');

  useEffect(() => {
    // Get code from URL if not provided as prop
    if (!code) {
      const urlParams = new URLSearchParams(window.location.search);
      const codeFromUrl = urlParams.get('code');
      if (codeFromUrl) {
        setCode(codeFromUrl);
      } else {
        setError('Invalid or missing reset code. Please request a new password reset link.');
      }
    }
  }, [code]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const validatePassword = (password) => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    if (!/(?=.*[a-z])/.test(password)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/(?=.*[A-Z])/.test(password)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/(?=.*\d)/.test(password)) {
      return 'Password must contain at least one number';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Validate passwords match
    if (formData.password !== formData.passwordConfirmation) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    // Validate password strength
    const passwordError = validatePassword(formData.password);
    if (passwordError) {
      setError(passwordError);
      setLoading(false);
      return;
    }

    if (!code) {
      setError('Invalid reset code. Please request a new password reset link.');
      setLoading(false);
      return;
    }

    try {
      await ApiService.resetPassword({
        code: code,
        password: formData.password,
        passwordConfirmation: formData.passwordConfirmation
      });
      
      setSuccess('Password reset successful! Redirecting to login...');
      
      // Redirect to login after 2 seconds
      setTimeout(() => {
        if (onBackToLogin) {
          onBackToLogin();
        }
      }, 2000);
    } catch (error) {
      console.error('Reset password error:', error);
      const errorMessage = error.response?.data?.error?.message || 
                          error.response?.data?.message || 
                          error.message || 
                          'Failed to reset password';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthPageContainer>
      <AuthCard>
        <LogoSection>
          <BrandTitle>GG TROPHY</BrandTitle>
          <BrandSubtitle>GAMERS REDEMPTION PORTAL</BrandSubtitle>
        </LogoSection>

        <Title>Set New Password</Title>
        <Description>
          Choose a strong password for your account.
        </Description>
        
        <Form onSubmit={handleSubmit}>
          <InputGroup>
            <Input
              type="password"
              name="password"
              placeholder="New Password"
              value={formData.password}
              onChange={handleChange}
              required
              disabled={loading || !code}
            />
          </InputGroup>

          <PasswordRequirements>
            <li>At least 8 characters</li>
            <li>One uppercase letter</li>
            <li>One lowercase letter</li>
            <li>One number</li>
          </PasswordRequirements>
          
          <InputGroup>
            <Input
              type="password"
              name="passwordConfirmation"
              placeholder="Confirm New Password"
              value={formData.passwordConfirmation}
              onChange={handleChange}
              required
              disabled={loading || !code}
            />
          </InputGroup>
          
          <Button type="submit" disabled={loading || !code}>
            {loading ? 'Resetting...' : 'Reset Password'}
          </Button>

          <BackButton type="button" onClick={onBackToLogin}>
            ← Back to Login
          </BackButton>

          {error && <Error>{error}</Error>}
          {success && <Success>{success}</Success>}
        </Form>
      </AuthCard>
    </AuthPageContainer>
  );
};

export default ResetPassword;
